import React, { useState, useEffect, useRef } from 'react';
import { generateText } from './ChatUI';
import TokenDisplay from './TokenDisplay';
import InsufficientTokensModal from './InsufficientTokensModal';
import TokenPurchase from './TokenPurchase';
import {
  translateWithBaidu,
  languageOptions,
  getDefaultTranslationLanguage,
  getAppLanguageFromTranslationLanguage,
} from '../utils/translation';
import { useTranslation } from 'react-i18next';
import { IoLanguage, IoChevronDown, IoDocumentTextOutline } from "react-icons/io5";
import { motion, AnimatePresence } from 'framer-motion';

const SelectedTextPanel = ({ selectedText, fullPDFText, isDarkMode, isAuthenticated = true, onLogin }) => {
  const { t, i18n } = useTranslation();
  const [translatedText, setTranslatedText] = useState('');
  const [showGenerating, setShowGenerating] = useState(false);
  const [finalSelectedText, setFinalSelectedText] = useState('');
  const [forceTokenUpdate, setForceTokenUpdate] = useState(0);
  const [showTokenDisplay, setShowTokenDisplay] = useState(false);
  const tokenDisplayTimeoutRef = useRef(null);
  const [showInsufficientWarning, setShowInsufficientWarning] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => getDefaultTranslationLanguage(i18n.language));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    const syncedLanguage = getDefaultTranslationLanguage(i18n.language);
    setSelectedLanguage(syncedLanguage);
    localStorage.setItem('selectedLanguage', syncedLanguage);
  }, [i18n.language]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFinalSelectedText(selectedText);
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedText]);

  useEffect(() => {
    if (finalSelectedText) {
      handleTranslation(finalSelectedText);
    }
  }, [finalSelectedText, selectedLanguage]); // Re-translate when language changes

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem('selectedLanguage', lang);

    const appLanguage = getAppLanguageFromTranslationLanguage(lang);
    if (appLanguage && appLanguage !== i18n.language) {
      i18n.changeLanguage(appLanguage);
    }
  };

  const handleTranslation = async (text) => {
    const translation = await translateWithBaidu(text, setShowGenerating, selectedLanguage);
    if (translation) {
      setTranslatedText(translation);
    } else {
      setTranslatedText('Translation failed');
    }
  };

  const startTokenDisplayTimer = () => {
    setShowTokenDisplay(true);

    // Clear any existing timeout
    if (tokenDisplayTimeoutRef.current) {
      clearTimeout(tokenDisplayTimeoutRef.current);
    }

    // Hide token display after 0.5 minute
    tokenDisplayTimeoutRef.current = setTimeout(() => {
      setShowTokenDisplay(false);
    }, 30000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tokenDisplayTimeoutRef.current) {
        clearTimeout(tokenDisplayTimeoutRef.current);
      }
    };
  }, []);

  const handleTranslate = async () => {
    startTokenDisplayTimer();
    setShowGenerating(true);
    setTranslatedText('');

    // Determine target language for prompt
    const targetLanguageName = languageOptions.find(lang => lang.value === selectedLanguage)?.label || 'Chinese';
    const prompt = `You are a great researcher, please translate the text to ${targetLanguageName}:`;

    // NOTE: The model parameter is currently ignored by the backend, which always uses 'GLM-4-Flash'
    // This is kept for consistency and to support future model selection features
    const model = 'GLM-4-Flash';
    const POLISH_MINIMUM_TOKENS = 1000;

    try {
      let fullResponse = '';
      await generateText(
        prompt,
        finalSelectedText,
        model,
        (content) => {
          fullResponse += content;
          setTranslatedText(fullResponse);
          setShowGenerating(false);
        },
        () => setForceTokenUpdate(prev => prev + 1),
        POLISH_MINIMUM_TOKENS
      );
    } catch (error) {
      if (error.message === 'INSUFFICIENT_TOKENS' ||
          error.message.includes('Insufficient tokens')) {
        setShowInsufficientWarning(true);
        setTranslatedText('');
      } else {
        setTranslatedText('An error occurred. Please try again.');
        console.error('Error:', error);
      }
    } finally {
      setShowGenerating(false);
    }
  };

  // Modify Biaoshu handler to use selected language
  const handleBiaoshu = async () => {
    startTokenDisplayTimer();
    setShowGenerating(true);
    setTranslatedText('');

    const targetLanguageName = languageOptions.find(lang => lang.value === selectedLanguage)?.label || 'Chinese';
    const prompt = `You are a great researcher, who is great at applying for NIH funds. Below is the text you want to use as context in your fund application. The application needs to be in ${targetLanguageName}. Please change the tone and language of the text to be suitable for use in this ${targetLanguageName} fund application:`;

    const model = 'GLM-4-Flash';
    const POLISH_MINIMUM_TOKENS = 1000;

    try {
      let fullResponse = '';
      await generateText(
        prompt,
        finalSelectedText,
        model,
        (content) => {
          fullResponse += content;
          setTranslatedText(fullResponse);
          setShowGenerating(false);
        },
        () => setForceTokenUpdate(prev => prev + 1),
        POLISH_MINIMUM_TOKENS
      );
    } catch (error) {
      if (error.message === 'INSUFFICIENT_TOKENS' ||
          error.message.includes('Insufficient tokens')) {
        setShowInsufficientWarning(true);
        setTranslatedText('');
      } else {
        setTranslatedText('An error occurred. Please try again.');
        console.error('Error:', error);
      }
    } finally {
      setShowGenerating(false);
    }
  };

  // Update copywrite handler to include language option
  const handleCopywrite = async () => {
    startTokenDisplayTimer();
    setShowGenerating(true);
    setTranslatedText('');

    const targetLanguageName = languageOptions.find(lang => lang.value === selectedLanguage)?.label || 'English';
    const prompt = `You are a great researcher, who is great at copywriting scientific papers. Please rewrite it to avoid being considered plagiarism. Your rewrite should be in ${targetLanguageName}:`;

    const model = 'GLM-4-Flash';
    const POLISH_MINIMUM_TOKENS = 1000;

    try {
      let fullResponse = '';
      await generateText(
        prompt,
        finalSelectedText,
        model,
        (content) => {
          fullResponse += content;
          setTranslatedText(fullResponse);
          setShowGenerating(false);
        },
        () => setForceTokenUpdate(prev => prev + 1),
        POLISH_MINIMUM_TOKENS
      );
    } catch (error) {
      if (error.message === 'INSUFFICIENT_TOKENS' ||
          error.message.includes('Insufficient tokens')) {
        setShowInsufficientWarning(true);
        setTranslatedText('');
      } else {
        setTranslatedText('An error occurred. Please try again.');
        console.error('Error:', error);
      }
    } finally {
      setShowGenerating(false);
    }
  };

  // Handle closing dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.language-dropdown')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-[#0d1117]' : 'bg-white'}`}>
      {/* Header */}
      <div className={`px-4 border-b ${isDarkMode ? 'border-[#1b2028]' : 'border-gray-100'} h-11 flex items-center justify-between flex-shrink-0`}>
        <div className="flex items-center gap-2">
          <IoDocumentTextOutline className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
          <h2 className={`text-[13px] font-semibold tracking-tight ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{t('translation.translateSelection')}</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Language dropdown */}
          <div className="relative language-dropdown" ref={langMenuRef}>
            <motion.button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 ${
                isDarkMode
                  ? 'bg-[#161b22] text-gray-300 hover:bg-[#1c2129] border border-[#21262d]'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
              } text-[11px] font-medium`}
              whileTap={{ scale: 0.97 }}
            >
              <IoLanguage size={13} />
              <span>{languageOptions.find(lang => lang.value === selectedLanguage)?.label?.split(' ')[0] || 'Language'}</span>
              <motion.span
                animate={{ rotate: dropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <IoChevronDown size={11} />
              </motion.span>
            </motion.button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  className={`absolute right-0 mt-1 z-10 w-44 rounded-xl shadow-lg ${
                    isDarkMode ? 'bg-[#161b22] border border-[#21262d]' : 'bg-white border border-gray-200'
                  } max-h-64 overflow-y-auto py-1 chat-scrollbar`}
                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  {languageOptions.map((language) => (
                    <button
                      key={language.value}
                      onClick={() => {
                        handleLanguageChange(language.value);
                        setDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-[12px] transition-colors duration-100 ${
                        selectedLanguage === language.value
                          ? (isDarkMode ? 'bg-sky-900/20 text-sky-300' : 'bg-blue-50 text-blue-700')
                          : (isDarkMode ? 'text-gray-300 hover:bg-[#1c2129]' : 'text-gray-700 hover:bg-gray-50')
                      }`}
                    >
                      {language.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {showTokenDisplay && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <TokenDisplay forceUpdate={forceTokenUpdate} />
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-grow overflow-y-auto p-4 flex flex-col gap-3 chat-scrollbar">
        {finalSelectedText ? (
          <>
            {/* Original Text */}
            <div className={`text-[13px] leading-relaxed flex-1 overflow-y-auto p-3.5 rounded-xl ${
              isDarkMode
                ? 'bg-[#161b22] text-gray-300 border border-[#21262d]'
                : 'bg-gray-50 text-gray-700 border border-gray-100'
            }`}>
              {finalSelectedText}
            </div>

            {/* Translated/Processed Text */}
            <div className={`text-[13px] leading-relaxed flex-1 overflow-y-auto p-3.5 rounded-xl ${
              isDarkMode
                ? 'bg-[#161b22] text-gray-200 border border-[#21262d]'
                : 'bg-gray-50 text-gray-800 border border-gray-100'
            }`}>
              {showGenerating ? (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className={`thinking-dot ${isDarkMode ? 'bg-purple-400' : 'bg-purple-500'}`}></span>
                    <span className={`thinking-dot ${isDarkMode ? 'bg-purple-400' : 'bg-purple-500'}`}></span>
                    <span className={`thinking-dot ${isDarkMode ? 'bg-purple-400' : 'bg-purple-500'}`}></span>
                  </div>
                  <span className={`text-[12px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('common.loading')}</span>
                </div>
              ) : translatedText}
            </div>
          </>
        ) : (
          <div className={`flex-1 flex items-center justify-center ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>
            <div className="text-center">
              <IoDocumentTextOutline className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-[12px]">{t('translation.selectText') || 'Select text in the PDF to translate'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className={`p-3 border-t ${isDarkMode ? 'border-[#1b2028]' : 'border-gray-100'} flex-shrink-0`}>
        <div className="flex gap-2">
          <button onClick={handleTranslate} className={`
            flex-1 font-medium py-2 px-2 text-[12px] rounded-xl
            transition-all duration-200
            ${isDarkMode
              ? 'bg-[#161b22] hover:bg-[#1c2129] text-gray-300 border border-[#21262d] hover:border-[#30363d]'
              : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300'
            }
          `}>
            {t('common.translate')}
          </button>
          <button onClick={handleCopywrite} className={`
            flex-1 font-medium py-2 px-2 text-[12px] rounded-xl
            transition-all duration-200
            ${isDarkMode
              ? 'bg-[#161b22] hover:bg-[#1c2129] text-gray-300 border border-[#21262d] hover:border-[#30363d]'
              : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300'
            }
          `}>
            {t('common.copywrite')}
          </button>
          <button onClick={handleBiaoshu} className={`
            flex-1 font-medium py-2 px-2 text-[12px] rounded-xl
            transition-all duration-200
            ${isDarkMode
              ? 'bg-[#161b22] hover:bg-[#1c2129] text-gray-300 border border-[#21262d] hover:border-[#30363d]'
              : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300'
            }
          `}>
            {t('common.biaoshu')}
          </button>
        </div>
      </div>

      {/* Modals */}
      {showInsufficientWarning && (
        <InsufficientTokensModal
          onClose={() => setShowInsufficientWarning(false)}
          onPurchase={() => {
            setShowInsufficientWarning(false);
            setShowTokenPurchase(true);
          }}
          requiredTokens={1000}
          currentBalance={currentBalance}
          isDarkMode={isDarkMode}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
        />
      )}

      {showTokenPurchase && (
        <TokenPurchase
          onClose={() => setShowTokenPurchase(false)}
          onSuccess={() => {
            setForceTokenUpdate(prev => prev + 1);
            startTokenDisplayTimer();
          }}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default SelectedTextPanel;
